/**
 * RetroDialog — canonical modal shell for the user-facing app.
 *
 * Wraps shadcn Dialog primitives with the "1980s retro / comic book"
 * treatment: 3px border, 8px hard drop-shadow, mono title, slot-based
 * header / body / footer that match `.modal-shell-*` classes in index.css.
 *
 * Usage:
 *   <RetroDialog open={open} onOpenChange={setOpen}>
 *     <RetroDialogHeader title="You found it! 🎧" description="…optional sub…" />
 *     <RetroDialogBody> …content… </RetroDialogBody>
 *     <RetroDialogFooter>
 *       <button className="btn-comic-ghost">Cancel</button>
 *       <button className="btn-comic-primary">Save</button>
 *     </RetroDialogFooter>
 *   </RetroDialog>
 *
 * Use the 4-tier button classes (.btn-comic-primary / -secondary / -ghost
 * / -danger) inside the footer so every modal has consistent CTAs.
 */
import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface RetroDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
  /** max-width Tailwind class — defaults to max-w-lg */
  size?: "sm" | "md" | "lg" | "xl";
}

const sizeMap = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
};

export function RetroDialog({
  open,
  onOpenChange,
  children,
  className,
  size = "lg",
}: RetroDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "modal-shell gap-0 overflow-hidden",
          sizeMap[size],
          className,
        )}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}

interface HeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
}

export function RetroDialogHeader({ title, description, className }: HeaderProps) {
  return (
    <div className={cn("modal-shell-header", className)}>
      <DialogTitle className="modal-shell-title">{title}</DialogTitle>
      {description ? (
        <DialogDescription className="modal-shell-desc">{description}</DialogDescription>
      ) : null}
    </div>
  );
}

export function RetroDialogBody({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn("modal-shell-body", className)}>{children}</div>;
}

export function RetroDialogFooter({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn("modal-shell-footer", className)}>{children}</div>;
}
